/**
 * Reveals the navigation "Live" chip only while chat can actually be answered.
 *
 * Gorgias exposes two questions and they disagree for most of the day:
 * `isChatOnline()` is true only while an agent is present, `isBusinessHours()`
 * is true for the whole configured window. Measured midday on a weekday,
 * isChatOnline() was false while isBusinessHours() was true — so which one
 * drives the chip is a merchandising decision, and it comes from `data-signal`.
 *
 * Availability is resolved ONCE per page at module level rather than per
 * element. The header reparents its nodes while it hydrates (the overflow list
 * moves top-level items, the drawer re-renders), so an element that ties the
 * lookup to its own connect/disconnect lifecycle loses the result to that race —
 * observed on the preview theme, where the chip stayed hidden even though
 * isBusinessHours() returned true throughout. A shared promise cannot be
 * disturbed by DOM moves, and every instance awaits the same answer.
 *
 * The chip ships hidden and every failure path leaves it hidden: no Gorgias, a
 * blocked bundle, a throwing API, a timeout. Promising a live reply that never
 * comes is worse than not advertising chat at all.
 */

/** Gorgias is an app embed, so the global appears well after the theme's JS. */
const ANSWER_WAIT_MS = 20000;
const POLL_MS = 500;

/**
 * `?chat_live=1` shows the chip and `?chat_live=0` hides it, whatever Gorgias
 * says. Chat is only answerable for part of the day, so without this the chip
 * cannot be reviewed on an evening or a weekend, and the alternative - setting
 * the flag by hand in a console - is not something a person reviewing the page
 * on a phone can do. It only decides whether a decorative label is drawn, so
 * there is nothing to protect: a visitor who found the parameter would show
 * themselves a "Live" chip and reach the same contact page either way.
 *
 * @returns {boolean|null} The forced answer, or null when the parameter is absent.
 */
function forcedAnswer() {
  const value = new URLSearchParams(window.location.search).get('chat_live');
  if (value === null) return null;
  return value !== '0' && value !== 'false';
}

/** @type {Map<string, Promise<boolean>>} */
const answers = new Map();

/**
 * Resolves whether chat is answerable for the given signal. Memoised per signal
 * so N chips cost one lookup.
 *
 * Polls for an affirmative rather than trusting the first reply. Gorgias
 * documents these methods as reliable only once the widget has initialised, and
 * before that `isBusinessHours()` answers false — indistinguishable from a real
 * "closed". Waiting for `ready` would be the clean gate, but it never fires on
 * this store: a second chat embed makes Gorgias log "Another chat was detected"
 * and skip, after which neither `ready` nor `gorgias-widget-loaded` arrives
 * even though the API still answers. Caching that early false made the chip
 * appear on roughly one load in three.
 *
 * So: a `true` is definitive and resolves immediately; a `false` only becomes
 * final at the deadline. Failing to a hidden chip is the safe direction.
 *
 * @param {string} signal - 'agent' or 'hours'.
 * @returns {Promise<boolean>}
 */
function isAnswerable(signal) {
  const cached = answers.get(signal);
  if (cached) return cached;

  const forced = forcedAnswer();
  if (forced !== null) {
    const settled = Promise.resolve(forced);
    answers.set(signal, settled);
    return settled;
  }

  const answer = new Promise((resolve) => {
    const deadline = Date.now() + ANSWER_WAIT_MS;

    const ask = async () => {
      const chat = window.GorgiasChat;
      if (chat) {
        try {
          const open = signal === 'agent' ? await chat.isChatOnline() : await chat.isBusinessHours();
          if (open) return resolve(true);
        } catch {
          // Not initialised yet, or the bundle is blocked. Retry until deadline.
        }
      }
      if (Date.now() >= deadline) return resolve(false);
      setTimeout(ask, POLL_MS);
    };

    ask();
  });

  answers.set(signal, answer);
  return answer;
}

/**
 * The answer is published as one attribute on the root element rather than on
 * each chip, because the header morphs its own markup during hydration and that
 * resets attributes on the chips back to what the server sent — silently
 * undoing a per-element flag on roughly one load in eight, with no second
 * connectedCallback to re-apply it. The root element is outside everything the
 * header touches, and one flag covers every chip on the page including any the
 * header adds later.
 */
class NavLiveChip extends HTMLElement {
  connectedCallback() {
    const signal = this.dataset.signal;
    if (signal === 'always') return;

    isAnswerable(signal === 'agent' ? 'agent' : 'hours').then((open) => {
      if (open) document.documentElement.dataset.chatLive = '';
    });
  }
}

if (!customElements.get('nav-live-chip')) {
  customElements.define('nav-live-chip', NavLiveChip);
}
